import {ProfileReference} from '@github-ui/profile-reference'
import {TrashIcon} from '@primer/octicons-react'
import {Link} from '@primer/react'
import {graphql} from 'react-relay'
import {useFragment} from 'react-relay/hooks'

import {LABELS} from '../constants/labels'
import {createIssueEventExternalUrl} from '../utils/urls'
import type {CommentDeletedEvent$key} from './__generated__/CommentDeletedEvent.graphql'
import styles from './CommentDeletedEvent.module.css'
import {TimelineRow} from './row/TimelineRow'

type CommentDeletedEventProps = {
  queryRef: CommentDeletedEvent$key
  issueUrl: string
  onLinkClick?: (event: MouseEvent) => void
  highlightedEventId?: string
  refAttribute?: React.MutableRefObject<HTMLDivElement | null>
}

export function CommentDeletedEvent({
  queryRef,
  issueUrl,
  onLinkClick,
  highlightedEventId,
  refAttribute,
}: CommentDeletedEventProps): React.ReactElement {
  const {actor, createdAt, deletedCommentAuthor, databaseId} = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment CommentDeletedEvent on CommentDeletedEvent {
        databaseId
        createdAt
        actor {
          ...TimelineRowEventActor
        }
        deletedCommentAuthor {
          __typename
          login
          ... on User {
            name
          }
          ... on Mannequin {
            name
          }
          ... on EnterpriseUserAccount {
            name
          }
        }
      }
    `,
    queryRef,
  )

  const highlighted = String(databaseId) === highlightedEventId
  const deletedCommentAuthorLogin = deletedCommentAuthor?.login
  const deletedCommentAuthorName = 'name' in (deletedCommentAuthor ?? {}) ? deletedCommentAuthor?.name : null

  return (
    <TimelineRow
      highlighted={highlighted}
      refAttribute={refAttribute}
      actor={actor}
      createdAt={createdAt}
      deepLinkUrl={createIssueEventExternalUrl(issueUrl, databaseId)}
      onLinkClick={onLinkClick}
      leadingIcon={TrashIcon}
    >
      <TimelineRow.Main>
        {`${LABELS.timeline.deletedACommentFrom} `}
        <Link href={`/${deletedCommentAuthorLogin}`} inline className={styles.deletedCommentAuthorLink}>
          <ProfileReference
            login={deletedCommentAuthorLogin ?? ''}
            profileName={deletedCommentAuthorName}
            isAgent={false}
          />
        </Link>{' '}
      </TimelineRow.Main>
    </TimelineRow>
  )
}
